import { useEffect, useState, type RefObject } from "react";
import { Button } from "react-aria-components";

import type { MerryChatMinimapItem } from "@/shared/ui/MerryChatMinimap";


interface FormalConversationRulerProps {
  items: readonly MerryChatMinimapItem[];
  onNavigate(id: string): void;
  scrollRef: RefObject<HTMLDivElement | null>;
}

export function FormalConversationRuler({ items, onNavigate, scrollRef }: FormalConversationRulerProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const update = () => {
      const top = scroller.getBoundingClientRect().top + 1;
      const anchors = [...scroller.querySelectorAll<HTMLElement>("[data-conversation-ruler-id]")];
      const active = anchors.find((anchor) => {
        const bounds = anchor.getBoundingClientRect();
        return bounds.top <= top && bounds.bottom > top;
      }) ?? anchors.find((anchor) => anchor.getBoundingClientRect().bottom > top);
      setActiveId(active?.dataset.conversationRulerId ?? null);
    };
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(scroller);
    for (const child of scroller.children) resizeObserver.observe(child);
    const contentObserver = new MutationObserver(() => {
      for (const child of scroller.children) resizeObserver.observe(child);
      update();
    });
    contentObserver.observe(scroller, { childList: true, subtree: true, characterData: true });
    scroller.addEventListener("scroll", update, { passive: true });
    update();
    return () => {
      resizeObserver.disconnect();
      contentObserver.disconnect();
      scroller.removeEventListener("scroll", update);
    };
  }, [items, scrollRef]);

  useEffect(() => {
    const clearPreview = () => setPreviewIndex(null);
    window.addEventListener("resize", clearPreview);
    return () => window.removeEventListener("resize", clearPreview);
  }, []);

  if (items.length === 0) return null;
  const center = Math.max(0, items.findIndex((item) => item.id === activeId));
  const preview = previewIndex === null ? null : items[previewIndex];

  return (
    <nav aria-label="对话进度" className="formal-conversation-ruler" onMouseLeave={() => setPreviewIndex(null)}>
      <div className="formal-conversation-ruler__ticks">
        {items.map((item, index) => {
          const distance = Math.abs(index - center);
          return (
            <Button
              aria-current={item.id === activeId ? "step" : undefined}
              aria-label={`${index + 1}/${items.length} · ${item?.accessibleLabel ?? "对话"}`}
              className="formal-conversation-ruler__tick"
              data-lens={distance === 0 ? "center" : distance === 1 ? "near" : undefined}
              key={item.id}
              onFocus={() => setPreviewIndex(index)}
              onBlur={() => setPreviewIndex(null)}
              onHoverChange={(hovered) => setPreviewIndex(hovered ? index : null)}
              onPress={() => item && onNavigate(item.id)}
            >
              <span aria-hidden="true" />
            </Button>
          );
        })}
      </div>
      {preview ? (
        <div className="formal-conversation-ruler__preview" role="status" >
          <small>{preview.preview.meta}</small>
          <span>{preview.preview.summary}</span>
        </div>
      ) : null}
    </nav>
  );
}
